import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Database } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const UniversalLookup = ({ 
  isOpen = false, 
  onClose = () => {}, 
  title = '', 
  type = '', 
  domainId = '', 
  filters = {}, 
  onSelect = () => {} 
}) => {
  const { t, i18n } = useTranslation(); // 🎯 تفعيل الـ Hook
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const scrollRef = useRef(null);
  const searchTimeout = useRef(null);

  const filtersString = JSON.stringify(filters || {});

  // 1. دالة جلب البيانات مع دعم البيدجينج والبحث على السيرفر
  const fetchLookupData = useCallback(async (pageNum, searchTerm = '', isNewSearch = false) => {
    if (!isOpen) return;

    if (isNewSearch) setLoading(true);
    else setLoadingMore(true);

    const url = domainId 
      ? `/api/domains/${domainId}` 
      : `/api/lookups/${type}`;

    try {
      const parsedFilters = JSON.parse(filtersString); // 🎯 نرجعها كائن تاني قبل الإرسال
      const response = await axios.get(url, {
        params: {
          ...parsedFilters,
          page: pageNum,
          limit: 50,
          search: searchTerm 
        }
      });

      const result = response.data;
      const newItems = result.data || result.values || (Array.isArray(result) ? result : []);
      const total = result.total || 0;
      const moreAvailable = result.has_more !== undefined ? result.has_more : newItems.length === 50;

      setData(prev => isNewSearch ? newItems : [...prev, ...newItems]);
      setHasMore(moreAvailable);
      setTotalRecords(total);
      
    } catch (err) {
      console.error("❌ Lookup Error:", err);
      if (isNewSearch) setData([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isOpen, type, domainId, filtersString]);

  // 2. تفعيل البحث (Debounce)
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchLookupData(1, search, true);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [search, isOpen, fetchLookupData]); // 🎯 أضفنا fetchLookupData للمصفوفة عشان الـ warnings

  // 3. مراقبة السكروول (Infinite Scroll)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 30 && hasMore && !loadingMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLookupData(nextPage, search, false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-[650px] shadow-2xl border border-gray-300 flex flex-col max-h-[85vh] rounded-sm overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header - Maximo Style */}
        <div className="bg-[#1a3353] text-white px-4 py-3 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-2">
            <Database size={14} className="text-blue-300" />
            <span className="text-xs font-bold uppercase tracking-wider">{t('universalLookup.selectValue')} {title}</span>
            {totalRecords > 0 && (
              <span className="bg-blue-600/50 px-2 py-0.5 rounded text-[9px] text-blue-100 ml-2 border border-blue-700">
                {totalRecords} {t('universalLookup.total')}
              </span>
            )}
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors">
            <X size={18}/>
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-3 bg-gray-50 border-b border-gray-200">
          <div className="relative group">
            <input 
              className="w-full border border-gray-300 p-2 pl-9 text-xs outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all rounded shadow-sm"
              placeholder={t('universalLookup.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <Search className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-blue-600 transition-colors" size={16} />
          </div>
        </div>

        {/* Data Grid - Scroll Event Added */}
        <div 
          className="overflow-y-auto flex-1 bg-white min-h-[350px] custom-scrollbar"
          onScroll={handleScroll}
          ref={scrollRef}
        >
          <table className="w-full text-[11px] border-collapse">
            <thead className="bg-slate-100 sticky top-0 border-b z-10 shadow-sm text-gray-600 font-bold uppercase tracking-tighter">
              <tr>
                <th className={`p-3 ${isRTL ? 'text-right border-l' : 'text-left border-r'}  w-1/3`}>{t('universalLookup.valueCode')}</th>
                <th className={`p-3 ${isRTL ? 'text-right border-l' : 'text-left border-r'}  w-1/3`}>{t('universalLookup.description')}</th>
                {type !=='sites' && <th className={`p-3 ${isRTL ? 'text-right border-l' : 'text-left border-r'}  w-1/3`}>{t('universalLookup.site')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && page === 1 ? (
                <tr>
                  <td colSpan="3" className="p-20 text-center text-blue-800 font-black">
                    <Loader2 className="animate-spin inline mr-3" size={24}/> 
                    <span className="uppercase tracking-[0.2em]">{t('universalLookup.synchronizing')}</span>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="3" className="p-20 text-center text-gray-400 italic font-bold uppercase tracking-widest">
                    {t('universalLookup.noRecords')}
                  </td>
                </tr>
              ) : (
                <>
                  {data.map((item, idx) => (
                    <tr 
                      key={`${idx}-${item.id || idx}`} 
                      className="border-b border-gray-100 hover:bg-[#fff9db] cursor-pointer transition-all group"
                      onClick={() => onSelect(item)}
                    >
                      <td className="p-3 font-black text-[#005495] border-r uppercase group-hover:text-blue-700">
                        {item.value || item.mrnum || item.prnum || item.tagoutnum || item.hazardnum || item.precautionnum || item.ponum || item.personid || item.jpnum || item.role_id || item.location || item.id || item.taxid || item.assetnum || item.safetypnum || item.failure_class || item.craft_code || item.slanum || item.site_id || item.comp_num || item.toolnum || item.itemnum || item.meter_num || item.feature_id || item.org_id || item.classificationid}
                      </td>
                      <td className="p-3 text-gray-600 font-bold italic">
                        {item.description || item.ldesc || item.meter_name || item.name || item.displayname || item.script_name || item.skill_level || "---"}
                      </td>
                      {type !=='sites' && (
                        <td className="p-3 font-black text-[#005495] border-l uppercase group-hover:text-blue-700">
                          {item.site_id || item.siteid}
                        </td>
                      )}
                    </tr>
                  ))}
                  
                  {loadingMore && (
                    <tr className="bg-blue-50/50">
                      <td colSpan="3" className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-blue-600" size={14}/>
                          <span className="font-black text-[9px] text-blue-600 uppercase">{t('universalLookup.loadingMore')}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table> 
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center shadow-inner">
          <span className="text-[10px] text-gray-400 font-bold uppercase">
              {t('universalLookup.results')} {data.length} / {totalRecords}
          </span>
          <button 
            onClick={onClose} 
            className="bg-white border border-gray-300 px-6 py-1.5 text-[11px] font-black text-gray-600 hover:bg-gray-100 active:scale-95 transition-all shadow-sm rounded uppercase"
          >
            {t('universalLookup.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UniversalLookup;